import type { Guild } from './api';

const LAST_SERVER_KEY_PREFIX = 'borgee:last-server:';

export type ServerSelection =
  | { kind: 'selected'; guildId: string }
  | { kind: 'removed-fallback'; guildId: string }
  | { kind: 'post-authentication-fallback'; guildId: string }
  | { kind: 'inaccessible' }
  | { kind: 'empty' };

const storageKey = (userId: string) => `${LAST_SERVER_KEY_PREFIX}${userId}`;

export function loadLastServerId(userId: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

export function rememberLastServerId(userId: string, guildId: string): void {
  try {
    window.localStorage.setItem(storageKey(userId), guildId);
  } catch {
    // Storage is an optimization. Routing still works when it is unavailable.
  }
}

/**
 * Chooses the Server without probing an arbitrary route id.
 *
 * An accessible route wins. A route that names the last selected Server but is
 * no longer present is a removed membership and falls back deterministically.
 * Any other unknown route is an untrusted deep link and stays on the
 * non-disclosing not-found path. The immediately post-authentication bootstrap
 * may explicitly opt into a safe fallback from the membership list; it never
 * probes the route-supplied id.
 */
export function resolveServerSelection(
  guilds: readonly Guild[],
  routeGuildId: string | undefined,
  rememberedGuildId: string | null,
  options?: { postAuthenticationFallbackGuildId?: string },
): ServerSelection {
  const accessible = new Set(guilds.map((guild) => guild.id));

  if (routeGuildId && accessible.has(routeGuildId)) {
    return { kind: 'selected', guildId: routeGuildId };
  }
  if (routeGuildId) {
    if (routeGuildId === rememberedGuildId && guilds[0]) {
      return { kind: 'removed-fallback', guildId: guilds[0].id };
    }
    const fallbackGuildId = options?.postAuthenticationFallbackGuildId;
    if (fallbackGuildId && accessible.has(fallbackGuildId)) {
      return { kind: 'post-authentication-fallback', guildId: fallbackGuildId };
    }
    return { kind: 'inaccessible' };
  }
  if (rememberedGuildId && accessible.has(rememberedGuildId)) {
    return { kind: 'selected', guildId: rememberedGuildId };
  }
  if (guilds[0]) return { kind: 'selected', guildId: guilds[0].id };
  return { kind: 'empty' };
}
